import React, {useEffect, useMemo, useState} from 'react';
import {createPortal} from "react-dom";
import {useDispatch, useSelector} from "react-redux";
import {closeDrawer} from "../../../../../../redux/slice/opnerSlice";
import {CodeSpecNum} from "../../../../../../constant/CodeSpecNum";
import {createAssetMemo} from "../../../../../../api/asset/AssetService";
import {skeleton} from "../../../../../../redux/slice/apiSlice";
import {handleActiveError, handleActiveToast} from "../../../../../../components/sonner/ToastBasic";
import SimmsTextArea from "../../../../../../components/input/SimmsTextArea";
import {inputOnChange} from "../../../../../../components/commonmethod/simmsInputMethod";
import SimmsInput from "../../../../../../components/input/SimmsInput";
import {onCreateClickListener} from "../../../../../../components/commonmethod/simmsDrawerMethod";
import Selector from "../../../../../../components/selector/Selector";
import {formatSimmsDateTimeKorean} from "../../../../../../components/commonmethod/CommonMethod";
import "./assetMemoModal.css";

const EMPTY_CONTENT = {
    code: '',
    title: '',
    content: '',
};

function AssetMemo({id}) {
    const open = useSelector(state => state.opener.drawer['AssetMemo'] || false);
    const dispatch = useDispatch();
    const [content, setContent] = useState(EMPTY_CONTENT);
    const [saving, setSaving] = useState(false);

    const registeredAt = useMemo(() => formatSimmsDateTimeKorean(new Date().toISOString()), [open]);
    const targetCount = id?.length ?? 0;
    const requireds = ["code", "title"];

    useEffect(() => {
        dispatch(skeleton(false));
    }, [dispatch]);

    useEffect(() => {
        if (!open) return;

        setContent(EMPTY_CONTENT);
        setSaving(false);

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                dispatch(closeDrawer({key: 'AssetMemo'}));
            }
        };
        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open, dispatch]);

    const closeModal = () => {
        dispatch(closeDrawer({key: 'AssetMemo'}));
    };

    const createAssetMemoHandler = async () => {
        if (saving) return;

        if (requireds.some((field) => !content[field])) {
            alert("필수값이 누락되었습니다.");
            return;
        }

        const contentArray = id.map((assetId) => ({
            assetId,
            createdAt: new Date().toISOString(),
            code: content.code,
            title: content.title,
            content: content.content,
        }));

        setSaving(true);
        try {
            const response = await createAssetMemo(contentArray);
            if (response.status === 200) {
                handleActiveToast({message: "저장 되었습니다."});
                closeModal();
            } else if (response.status == 201) {
                handleActiveError({message: response.data.title, description: response.data.reason});
            }
        } finally {
            setSaving(false);
        }
    };

    if (!open || typeof document === "undefined") {
        return null;
    }

    return createPortal(
        <div className="asset-memo-modal-root">
            <div className="asset-memo-modal-overlay" onClick={closeModal} aria-hidden />
            <div
                className="asset-memo-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="asset-memo-modal-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="asset-memo-modal__header">
                    <div>
                        <h3 id="asset-memo-modal-title" className="asset-memo-modal__title">
                            자산기록 등록
                        </h3>
                        <p className="asset-memo-modal__meta">
                            선택 자산 {targetCount}건에 동일한 기록을 등록합니다.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="asset-memo-modal__close"
                        onClick={closeModal}
                        aria-label="닫기"
                    >
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                                d="M18 6L6 18M6 6l12 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>

                <div className="asset-memo-modal__body">
                    <p className="asset-memo-modal__hint">
                        필수 값은 <span className="asset-memo-modal__required">*</span>로 표시됩니다.
                    </p>

                    <div className="asset-memo-modal__field">
                        <span className="asset-memo-modal__label">등록일시</span>
                        <div className="asset-memo-modal__value">{registeredAt}</div>
                    </div>

                    <div className="asset-memo-modal__field">
                        <span className="asset-memo-modal__label">
                            기록관리 코드 <span className="asset-memo-modal__required">*</span>
                        </span>
                        <Selector
                            auth
                            onChange={(v) => onCreateClickListener(setContent, "code", content.code, v)}
                            data={content.code}
                            entity={"code"}
                            recursive
                            parentKey={CodeSpecNum.AssetMemo}
                        />
                    </div>

                    <div className="asset-memo-modal__field">
                        <span className="asset-memo-modal__label">
                            제목 <span className="asset-memo-modal__required">*</span>
                        </span>
                        <SimmsInput
                            onChange={(v) => inputOnChange(setContent, "title", v, true)}
                            data={content.title}
                        />
                    </div>

                    <div className="asset-memo-modal__field">
                        <span className="asset-memo-modal__label">내용</span>
                        <SimmsTextArea
                            onChange={(v) => inputOnChange(setContent, "content", v, true)}
                            data={content.content}
                            style={{minHeight: "120px", width: "100%", margin: 0}}
                        />
                    </div>
                </div>

                <div className="asset-memo-modal__footer">
                    <button
                        type="button"
                        className="asset-memo-modal__btn is-muted"
                        onClick={closeModal}
                        disabled={saving}
                    >
                        취소
                    </button>
                    <button
                        type="button"
                        className="asset-memo-modal__btn is-primary"
                        onClick={createAssetMemoHandler}
                        disabled={saving}
                    >
                        {saving ? "등록 중…" : "등록"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

export default AssetMemo;
